import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close, PaymentsOutlined, SendOutlined, VpnKeyOutlined } from "@mui/icons-material";
import { sendConciliationFinal } from "modules/finops/conciliaciones/services/financieraService";

export default function ModalConciliar({ row, onClose, onSuccess }) {
  const [value, setValue] = useState("");
  const [peajeValues, setPeajeValues] = useState({});
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const porPeaje =
    String(row.tipo_pago || "")
      .trim()
      .toUpperCase() === "POR_PEAJE";
  let more = row.more ?? row.original?.more;
  if (typeof more === "string") {
    try {
      more = JSON.parse(more);
    } catch (_) {
      more = null;
    }
  }
  const peajes =
    more?.estaciones && !Array.isArray(more.estaciones)
      ? Object.keys(more.estaciones).filter((name) => name.trim())
      : [];
  const amount = porPeaje
    ? peajes.reduce((total, name) => total + Number(peajeValues[name] || 0), 0)
    : Number(value);
  const valid =
    Number.isSafeInteger(amount) &&
    amount > 0 &&
    (porPeaje
      ? peajes.length > 0 &&
        peajes.every(
          (name) =>
            peajeValues[name] !== "" &&
            Number.isSafeInteger(Number(peajeValues[name])) &&
            Number(peajeValues[name]) > 0
        )
      : value !== "");
  const submit = async (event) => {
    event.preventDefault();
    if (!valid || pending.current) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    pending.current = true;
    setSending(true);
    setError("");
    try {
      await sendConciliationFinal({
        key_concil: row.key_concil,
        code: row.concesion,
        value_final: amount,
        ...(porPeaje
          ? { finops_values: peajes.map((name) => ({ name, value: Number(peajeValues[name]) })) }
          : {}),
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo enviar el valor a pagar.");
    } finally {
      pending.current = false;
      setSending(false);
    }
  };
  return (
    <Dialog
      open
      onClose={() => !pending.current && onClose()}
      disableEscapeKeyDown={sending}
      fullWidth
      maxWidth="sm"
      aria-labelledby="conciliation-final-title"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", m: { xs: 2, sm: 3 } } }}
    >
      <Box
        component="form"
        onSubmit={submit}
        sx={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}
      >
        <DialogTitle
          sx={{ background: "linear-gradient(110deg,#153349,#135e65)", color: "#fff", p: 2.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.12)",
              }}
            >
              <PaymentsOutlined sx={{ color: "#fff", fontSize: 25 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                id="conciliation-final-title"
                sx={{ color: "#fff", fontSize: 18, fontWeight: 700 }}
              >
                Conciliar concesión
              </Typography>
              <Typography sx={{ color: "#dbecef", fontSize: 13, mt: 0.25 }}>
                {porPeaje
                  ? "Define el valor a pagar por cada peaje"
                  : "Define el valor final para el pago de Financiera"}
              </Typography>
            </Box>
            <IconButton
              aria-label="Cerrar conciliación"
              onClick={onClose}
              disabled={sending}
              sx={{ color: "#fff" }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: "24px !important" }}>
          <Box
            sx={{ border: "1px solid #dce5eb", borderRadius: 2, p: 2, mb: 3, bgcolor: "#f7f9fb" }}
          >
            <Typography
              sx={{ fontSize: 11, fontWeight: 700, color: "#526477", letterSpacing: 0.6 }}
            >
              CONCESIÓN
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: "#172b3a",
                mt: 0.5,
                overflowWrap: "anywhere",
              }}
            >
              {String(row.name || row.realName || row.concesion).toUpperCase()}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 3 }}
              sx={{ mt: 1.5 }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <VpnKeyOutlined sx={{ fontSize: 16, color: "#0b4f6c" }} />
                <Typography sx={{ fontSize: 13, color: "#526477" }}>
                  Llave:{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#172b3a" }}>
                    {row.key_concil}
                  </Box>
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 13, color: "#526477" }}>
                Código:{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "#172b3a" }}>
                  {row.concesion}
                </Box>
              </Typography>
            </Stack>
          </Box>
          {porPeaje ? (
            <Stack spacing={2}>
              <Alert severity="info">
                Ingresa el valor autorizado para cada peaje. El total se enviará a Financiera con su
                desglose.
              </Alert>
              {!peajes.length && (
                <Alert severity="warning">
                  No se encontraron peajes asociados en la información de esta concesión.
                </Alert>
              )}
              {peajes.map((name) => (
                <Box key={name} sx={{ border: "1px solid #dce5eb", borderRadius: 2, p: 2 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#16485a", mb: 1.5 }}>
                    {name}
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    label={`Valor a pagar · ${name}`}
                    value={
                      peajeValues[name] ? Number(peajeValues[name]).toLocaleString("es-CO") : ""
                    }
                    onChange={(event) =>
                      setPeajeValues((previous) => ({
                        ...previous,
                        [name]: event.target.value.replace(/\D/g, ""),
                      }))
                    }
                    disabled={confirming}
                    inputProps={{ inputMode: "numeric" }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">COP</InputAdornment>,
                    }}
                    sx={{ "& input": { color: "#172b3a", fontWeight: 700 } }}
                  />
                </Box>
              ))}
              <Box sx={{ p: 2, bgcolor: "#edf5f7", borderRadius: 2 }}>
                <Typography sx={{ fontSize: 12, color: "#475569" }}>
                  Total a pagar por la concesión
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#16485a" }}>
                  ${amount.toLocaleString("es-CO")} COP
                </Typography>
              </Box>
            </Stack>
          ) : (
            <>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#172b3a", mb: 2 }}>
                Valor autorizado para pagar
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Valor a pagar a Financiera (COP)"
                value={value ? Number(value).toLocaleString("es-CO") : ""}
                onChange={(event) => setValue(event.target.value.replace(/\D/g, ""))}
                disabled={sending || confirming}
                inputProps={{ inputMode: "numeric" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: <InputAdornment position="end">COP</InputAdornment>,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { minHeight: 60, borderRadius: 2 },
                  "& input": {
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#172b3a",
                    fontVariantNumeric: "tabular-nums",
                  },
                  "& .MuiInputLabel-root": { fontSize: 14 },
                  "& .MuiFormHelperText-root": { fontSize: 12, mt: 1 },
                }}
                helperText="Ingresa el valor final en pesos."
              />
            </>
          )}
          {confirming && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              ¿Estás seguro de enviar a Financiera el valor de{" "}
              <strong>${amount.toLocaleString("es-CO")} COP</strong> para el pago de la concesión{" "}
              <strong>{String(row.name || row.realName || row.concesion).toUpperCase()}</strong>?
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: "1px solid #e2e8f0",
            bgcolor: "#f8fafc",
            flexWrap: "wrap",
          }}
        >
          <Button
            onClick={() => (confirming ? setConfirming(false) : onClose())}
            disabled={sending}
            variant="outlined"
            sx={{
              color: "#334155",
              borderColor: "#cbd5e1",
              textTransform: "none",
              px: 2.5,
              minHeight: 40,
            }}
          >
            {confirming ? (porPeaje ? "Editar valores" : "Revisar valor") : "Cancelar"}
          </Button>
          <Button
            type="button"
            onClick={submit}
            variant="contained"
            disabled={!valid || sending}
            startIcon={<SendOutlined />}
            sx={{
              bgcolor: "#0b4f6c",
              color: "#fff",
              textTransform: "none",
              px: 2.5,
              minHeight: 40,
              boxShadow: "none",
              "&:hover": { bgcolor: "#123b4e", boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#64748b" },
            }}
          >
            {sending
              ? "Enviando..."
              : confirming
              ? "Sí, enviar a Financiera"
              : porPeaje
              ? "Revisar valores"
              : "Enviar valor a pagar"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
ModalConciliar.propTypes = {
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
